import React, { useMemo } from "react";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";
import {
	loadCountryOptions,
	toCountrySelectValue,
} from "../../Services/masterCountriesService";

const getSelectTheme = (isDark) => (theme) => ({
	...theme,
	colors: {
		...theme.colors,
		primary: isDark ? "#6b7fd7" : "#24695c",
		primary25: isDark ? "#373737" : "#e6f7ff",
		primary50: isDark ? "#4a5464" : "#d6e8ff",
		neutral0: isDark ? "#262932" : "#ffffff",
		neutral20: isDark ? "#4a5464" : "#d0d5dd",
		neutral30: isDark ? "#5c6678" : "#cbd5e1",
		neutral40: isDark ? "#8b95a8" : "#98a2b3",
		neutral50: isDark ? "#a8b0be" : "#98a2b3",
		neutral80: isDark ? "#e8eaef" : "#344054",
	},
});

const getSelectStyles = (isDark, useMenuPortal) => ({
	control: (base, state) => ({
		...base,
		minHeight: 36,
		fontSize: "0.875rem",
		borderRadius: 8,
		borderColor: state.isFocused
			? isDark
				? "#6b7fd7"
				: "#24695c"
			: isDark
				? "#4a5464"
				: "#d0d5dd",
		boxShadow: state.isFocused
			? isDark
				? "0 0 0 3px rgba(107, 127, 215, 0.2)"
				: "0 0 0 3px rgba(36, 105, 92, 0.12)"
			: "none",
		"&:hover": {
			borderColor: isDark ? "#6b7fd7" : "#24695c",
		},
	}),
	menu: (base) => ({
		...base,
		fontSize: "0.875rem",
		zIndex: useMenuPortal ? undefined : 20,
	}),
	menuPortal: (base) => ({
		...base,
		zIndex: 2000,
	}),
	option: (base) => ({
		...base,
		fontSize: "0.875rem",
		padding: "8px 12px",
	}),
	placeholder: (base) => ({
		...base,
		color: isDark ? "#8b95a8" : "#98a2b3",
	}),
	singleValue: (base) => ({
		...base,
		color: isDark ? "#e8eaef" : "#344054",
	}),
});

/**
 * Reusable country AsyncSelect (POST /pharma/masterCountries).
 * value: country name string | option object | null
 * onChange: (option | null) => void  — full option for code / states API
 */
const CountrySelect = ({
	value = null,
	onChange,
	placeholder,
	isDisabled = false,
	inModal = true,
	className = "",
	minSearchLength = 2,
}) => {
	const { t } = useTranslation();
	const isDark = document.body.classList.contains("dark-only");
	const selectedValue = useMemo(() => toCountrySelectValue(value), [value]);
	const useMenuPortal = !inModal;

	const loadOptions = async (inputValue) => {
		const search = String(inputValue || "").trim();
		if (search.length < minSearchLength) return [];
		return loadCountryOptions(search);
	};

	return (
		<AsyncSelect
			className={className}
			classNamePrefix="country-select"
			cacheOptions
			defaultOptions={false}
			loadOptions={loadOptions}
			value={selectedValue}
			onChange={(option) => onChange?.(option || null)}
			isClearable
			isSearchable
			isDisabled={isDisabled}
			placeholder={placeholder || t("Search country...")}
			noOptionsMessage={({ inputValue }) =>
				String(inputValue || "").trim().length < minSearchLength
					? t("Type at least 2 characters")
					: t("No countries found")
			}
			loadingMessage={() => t("Loading...")}
			theme={getSelectTheme(isDark)}
			styles={getSelectStyles(isDark, useMenuPortal)}
			menuPortalTarget={
				useMenuPortal && typeof document !== "undefined" ? document.body : null
			}
			menuPosition={useMenuPortal ? "fixed" : "absolute"}
			menuPlacement="auto"
			menuShouldScrollIntoView={false}
		/>
	);
};

export default CountrySelect;
